import React, { useEffect, useMemo, useState } from "react";
import { Box, Grid, IconButton, Tab, Tabs } from "@mui/material";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";

import GetObjetivosAprendizajeOrganizacion from "../../ObjetivosAprendizaje/GetObjetivosAprendizajeOrganizacion";
import { useProyecto } from "../../../data/ProyectoContext";

type AsignaturaOrg = { id: string; nombre: string };

interface TabItem {
  idNivelAsignatura: string;
  nombreAsignatura: string;
}

interface Props {
  nombreNivel: string;
  asignaturaSeleccionada: AsignaturaOrg | null;

  setPromptData?: React.Dispatch<React.SetStateAction<any>>;
  objetivosSeleccionados?: any;
  setObjetivosSeleccionados?: React.Dispatch<React.SetStateAction<any>>;
  setIsOAAdded?: React.Dispatch<React.SetStateAction<boolean>>;
}

function TabPanel(props: {
  children: React.ReactNode;
  value: number;
  index: number;
}) {
  const { children, value, index } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`org-tab-panel-${index}`}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

function buildTabItem(a: AsignaturaOrg): TabItem {
  return {
    idNivelAsignatura: String(a.id),
    nombreAsignatura: String(a.nombre),
  };
}

function nextActiveIndex(current: number, deleteIndex: number, newLength: number) {
  if (newLength <= 0) return 0;
  if (current === deleteIndex) return deleteIndex === 0 ? 0 : deleteIndex - 1;
  if (current > deleteIndex) return current - 1;
  return current;
}

export default function TabsObjetivosOrganizacion({
  nombreNivel, // (no se usa aquí, se mantiene por compatibilidad)
  asignaturaSeleccionada,
  setPromptData,
  objetivosSeleccionados,
  setObjetivosSeleccionados,
  setIsOAAdded,
}: Props) {
  const { proyecto, setProyecto } = useProyecto();


  const tabs: TabItem[] = useMemo(() => {
    const arr = (proyecto as any)?.asignaturas_org_tabs;
    if (!Array.isArray(arr)) return [];
    return arr
      .map((x: any) => ({
        idNivelAsignatura: String(x?.idNivelAsignatura ?? x?.id ?? ""),
        nombreAsignatura: String(x?.nombreAsignatura ?? x?.nombre ?? ""),
      }))
      .filter((t: TabItem) => Boolean(t.idNivelAsignatura && t.nombreAsignatura));
  }, [(proyecto as any)?.asignaturas_org_tabs]);

  const [value, setValue] = useState(0);

 
  useEffect(() => {
    if (value > tabs.length - 1) {
      setValue(tabs.length > 0 ? tabs.length - 1 : 0);
    }
  }, [tabs.length, value]);

  const asignaturaKey = useMemo(() => {
    if (!asignaturaSeleccionada?.id) return "";
    return String(asignaturaSeleccionada.id);
  }, [asignaturaSeleccionada]);

  
  useEffect(() => {
    if (!asignaturaSeleccionada?.id || !asignaturaSeleccionada?.nombre) return;

    const nextTab = buildTabItem(asignaturaSeleccionada);

    setProyecto((prev: any) => {
      const prevTabs: TabItem[] = Array.isArray(prev?.asignaturas_org_tabs)
        ? prev.asignaturas_org_tabs
        : [];

      const existsIndex = prevTabs.findIndex(
        (t: any) => String(t?.idNivelAsignatura ?? t?.id) === nextTab.idNivelAsignatura
      );

      // ya existe entonces solo activar
      if (existsIndex >= 0) {
        setValue(existsIndex);
        return prev;
      }

      // no existe entonces agregar
      const nextTabs = [...prevTabs, nextTab];
      setValue(nextTabs.length - 1);

      return {
        ...prev,
        asignaturas_org_tabs: nextTabs,
        materias_org: nextTabs.map((t) => t.nombreAsignatura),
      };
    });

    
    if (typeof setPromptData === "function") {
      setPromptData((p: any) => {
        const pTabs: TabItem[] = Array.isArray(p?.asignaturas_org_tabs)
          ? p.asignaturas_org_tabs
          : [];
        const exists = pTabs.some(
          (t: any) =>
            String(t?.idNivelAsignatura ?? t?.id) === nextTab.idNivelAsignatura
        );
        const nextPTabs = exists ? pTabs : [...pTabs, nextTab];
        return {
          ...p,
          asignaturas_org_tabs: nextPTabs,
          materias_org: nextPTabs.map((t) => t.nombreAsignatura),
        };
      });
    }
  }, [asignaturaKey, asignaturaSeleccionada, setProyecto, setPromptData]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const clearLocalObjetivosForMateria = (materia: string) => {
    if (!setObjetivosSeleccionados) return;
    if (!objetivosSeleccionados || !Array.isArray(objetivosSeleccionados)) return;

    const updated = objetivosSeleccionados.filter(
      (item: any) => item?.nombreAsignatura !== materia
    );
    setObjetivosSeleccionados(updated);
  };

  const clearProyectoObjetivosForMateria = (materia: string) => {
    setProyecto((prev: any) => ({
      ...prev,
      objetivos_aprendizaje_org: Array.isArray(prev?.objetivos_aprendizaje_org)
        ? prev.objetivos_aprendizaje_org.filter(
            (item: any) => item?.materia !== materia
          )
        : prev.objetivos_aprendizaje_org,
    }));
  };

 
  const handleTabDelete = (deleteIndex: number) => {
    const tabToDelete = tabs[deleteIndex];
    if (!tabToDelete) return;

    clearLocalObjetivosForMateria(tabToDelete.nombreAsignatura);

    const nextTabs = tabs.filter((_, i) => i !== deleteIndex);
    const nextIndex = nextActiveIndex(value, deleteIndex, nextTabs.length);

    setValue(nextIndex);

    // Persistir el borrado
    setProyecto((prev: any) => ({
      ...prev,
      asignaturas_org_tabs: nextTabs,
      materias_org: nextTabs.map((t) => t.nombreAsignatura),
    }));

  
    clearProyectoObjetivosForMateria(tabToDelete.nombreAsignatura);

   
    if (typeof setPromptData === "function") {
      setPromptData((p: any) => ({
        ...p,
        asignaturas_org_tabs: nextTabs,
        materias_org: nextTabs.map((t) => t.nombreAsignatura),
      }));
    }

    if (nextTabs.length === 0 && setIsOAAdded) {
      setIsOAAdded(false);
    }
  };

  return (
    <Box>
      <Tabs
        value={value}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        aria-label="Objetivos de organización por asignatura"
        sx={{
          "& .MuiTabs-indicator": {
            backgroundColor: "#336B87",
            height: 3,
            width: "70%",
            borderRadius: 2,
          },
        }}
      >
        {tabs.map((tab, index) => (
          <Tab
            key={tab.idNivelAsignatura}
            id={`org-tab-${index}`}
            aria-controls={`org-tab-panel-${index}`}
            disableRipple
            label={
              <Grid
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  backgroundColor: "rgba(250, 219, 151, 1)",
                  color: "#336B87",
                  fontFamily: "Montserrat",
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  pl: 3,
                  pr: 3,
                  pt: 2,
                  pb: 2,
                  borderRadius: "10px",
                }}
              >
                {tab.nombreAsignatura}
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTabDelete(index);
                  }}
                  sx={{ ml: 1, color: "#336B87" }}
                >
                  <DeleteForeverRoundedIcon />
                </IconButton>
              </Grid>
            }
          />
        ))}
      </Tabs>

      {tabs.map((tab, index) => (
        <TabPanel key={tab.idNivelAsignatura} value={value} index={index}>
          <GetObjetivosAprendizajeOrganizacion
            idNivelAsignatura={tab.idNivelAsignatura}
          />
        </TabPanel>
      ))}
    </Box>
  );
}
